import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '../../layouts/AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useCases, useDeleteCase } from '../../hooks';
import { CasesTable } from '../../components/organisms/CasesTable';
import { FiltersPanel } from '../../components/organisms/FiltersPanel';
import { Pagination } from '../../components/molecules/Pagination';
import { LoadingOverlay } from '../../components/LoadingOverlay';
import { Button } from '../../components/atoms/Button';
import type { Case } from '@lnr-v2/types';
import { toast } from '../../utils/toast';
import { prefetchNextPage } from '../../lib/prefetchHelpers';
import { queryKeys } from '../../lib/queryKeys';
import { casesService } from '../../services/cases.service';
import { useMutation } from '@tanstack/react-query';
import styles from './CasesPage.module.css';

export const CasesPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation(); // Add useLocation
  const { user } = useAuth();

  const isLayoutMode = location.pathname === '/admin/layouts';

  const { data, loading, error, filters, updateFilter, refetch } = useCases();
  const { deleteCase, loading: deleting } = useDeleteCase();

  // Reorder mutation with rollback support
  const [reorderError, setReorderError] = useState(false);

  const reorderMutation = useMutation({
    mutationFn: (updates: Array<{ id: string; sortOrder: number }>) =>
      casesService.reorder(updates),
    onSuccess: () => {
      toast.success('Cases reordered successfully');
      refetch();
    },
    onError: () => {
      toast.error('Failed to reorder cases. Order restored.');
      setReorderError(true);
    },
  });

  const handleReorder = (updates: Array<{ id: string; sortOrder: number }>) => {
    reorderMutation.mutate(updates);
  };

  const handleReorderErrorHandled = () => {
    setReorderError(false);
  };

  const [caseToDelete, setCaseToDelete] = useState<Case | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [categories, setCategories] = useState<
    Array<{ id: string; name: string }>
  >([]);

  // Prefetch next page for faster pagination
  useEffect(() => {
    const pagination = data?.data?.pagination;
    if (pagination && pagination.page < pagination.totalPages) {
      const currentPage = filters.page || 1;
      prefetchNextPage(
        queryClient,
        queryKeys.cases.list,
        filters,
        currentPage,
        casesService.getAll
      );
    }
  }, [data, filters, queryClient]);

  const isAdmin =
    user?.role === 'SUPER_ADMIN' ||
    user?.role === 'ADMIN' ||
    user?.role === 'admin';

  // Fetch categories for filters
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        // For now, extract unique categories from the cases data
        if (data?.data?.items) {
          const uniqueCategories = Array.from(
            new Map(
              data.data.items
                .filter((c) => c.category)
                .map((c) => [
                  c.category!.id,
                  { id: c.category!.id, name: c.category!.name },
                ])
            ).values()
          );
          setCategories(uniqueCategories);
        }
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    };

    fetchCategories();
  }, [data]);

  const handleSelectCase = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((caseId) => caseId !== id) : [...prev, id]
    );
  };

  const handleEdit = (id: string) => {
    if (isLayoutMode) {
      navigate(`/admin/cases/${id}/edit?tab=layout`);
    } else {
      navigate(`/admin/cases/${id}/edit`);
    }
  };

  const handleView = (id: string) => {
    // For now, go to edit page. Can add a view-only mode later
    if (isLayoutMode) {
      navigate(`/admin/cases/${id}/edit?tab=layout`);
    } else {
      navigate(`/admin/cases/${id}/edit`);
    }
  };

  const handleDelete = (id: string) => {
    const caseItem = data?.data?.items?.find((c) => c.id === id);
    if (caseItem) {
      setCaseToDelete(caseItem);
      setShowDeleteConfirm(true);
    }
  };

  const confirmDelete = async () => {
    if (!caseToDelete) return;

    try {
      await deleteCase(caseToDelete.id);
      setShowDeleteConfirm(false);
      setCaseToDelete(null);
      toast.success('Case deleted successfully');
      await refetch();
    } catch (error) {
      console.error('Failed to delete case:', error);
      toast.error('Failed to delete case');
    }
  };

  const handleFiltersChange = (newFilters: Partial<typeof filters>) => {
    Object.entries(newFilters).forEach(([key, value]) => {
      updateFilter(key as keyof typeof filters, value);
    });
  };

  const handleClearFilters = () => {
    updateFilter('search', undefined);
    updateFilter('categoryId', undefined);
    updateFilter('status', undefined);
    updateFilter('isFeatured', undefined);
    updateFilter('page', 1);
  };

  if (loading && !data) {
    return (
      <AdminLayout>
        <LoadingOverlay isLoading={true} />
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className={styles.container}>
          <div className={styles.error}>
            <h3 className={styles.errorTitle}>Error loading cases</h3>
            <p className={styles.errorMessage}>{error.message}</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <h1 className={styles.title}>Cases</h1>
          {isAdmin && (
            <Button
              variant="primary"
              size="md"
              onClick={() => navigate('/admin/cases/new')}
            >
              + New Case
            </Button>
          )}
        </div>

        {/* Filters */}
        <FiltersPanel
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onClearFilters={handleClearFilters}
          categories={categories}
          loading={loading}
        />

        {/* Table */}
        <CasesTable
          cases={data?.data?.items || []}
          loading={loading}
          selectedIds={selectedIds}
          onSelectCase={handleSelectCase}
          onSelectAll={() => {
            if (selectedIds.length === data?.data?.items?.length) {
              setSelectedIds([]);
            } else {
              setSelectedIds(data?.data?.items?.map((c) => c.id) || []);
            }
          }}
          onEdit={isAdmin ? handleEdit : () => {}}
          onDelete={isAdmin ? handleDelete : () => {}}
          onView={handleView}
          onReorder={isAdmin ? handleReorder : undefined}
          reorderError={reorderError}
          onReorderErrorHandled={handleReorderErrorHandled}
        />

        {/* Pagination */}
        {data?.data?.pagination && data.data.pagination.totalPages > 1 && (
          <Pagination
            currentPage={data.data.pagination.page}
            totalPages={data.data.pagination.totalPages}
            totalItems={data.data.pagination.total}
            itemsPerPage={data.data.pagination.limit}
            onPageChange={(page) => updateFilter('page', page)}
            onItemsPerPageChange={(limit) => updateFilter('limit', limit)}
          />
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && caseToDelete && (
          <div className={styles.modal}>
            <div className={styles.modalContent}>
              <h3 className={styles.modalTitle}>Delete Case</h3>
              <p className={styles.modalMessage}>
                Are you sure you want to delete &ldquo;{caseToDelete.title}
                &rdquo;? This action cannot be undone.
              </p>
              <div className={styles.modalActions}>
                <Button
                  variant="ghost"
                  size="md"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setCaseToDelete(null);
                  }}
                  disabled={deleting}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  size="md"
                  onClick={confirmDelete}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
